import { useState } from "react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetHeading,
  SheetTitle,
  SheetDescription,
  SheetBody,
} from "~/react/ui/sheet"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { Tag, TagLabel } from "~/react/ui/tag"
import { Alert, AlertIcon, AlertContent, AlertTitle } from "~/react/ui/alert"
import { SeparatorWithWord } from "~/react/ui/separator"
import {
  campaigns_email_accounts_center_path,
  checkout_subscriptions_path,
  email_settings_campaigns_path,
  upgrade_welcome_index_path,
  new_smtp_imap_path,
  email_accounts_mailboxes_path,
} from "~/lib/routes"
import googleIcon from "~/images/connected_apps/google_icon.png"
import microsoftIcon from "~/images/connected_apps/microsoft_icon.png"
import AddEmailAccountSlotsModal from "./add_email_account_slots_modal"

type CreditCardInfo = {
  card_type: string
  last_4: string
  expiration_month: number | null
  expiration_year: string | null
}

type BillingInfo = {
  address: string | null
  city: string | null
  postal_code: string | null
  country_code: string | null
}

type ConnectEmailAccountsSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  isPremium: boolean
  isQuotaReached: boolean
  currentEmailAccountsCount: number
  currentMaxEmailAccounts: number
  absoluteMaxEmailAccounts: number
  planIncludedEmailAccounts: number
  pricePerSlot: number
  currencySymbol: string
  currencyFormat: string
  billingCycle: string
  subscriptionLevel: number
  creditCardInfo?: CreditCardInfo | null
  nextBillingDate?: string | null
  billingPeriodStart?: string | null
  stripePublicKey?: string | null
  billingInfo?: BillingInfo | null
  userEmail?: string | null
  cardholderName?: string | null
  hasCreditCard?: boolean
  filter?: "managed" | "connected"
}

export function ConnectEmailAccountsSheet({
  open,
  onOpenChange,
  isPremium,
  isQuotaReached,
  currentEmailAccountsCount,
  currentMaxEmailAccounts,
  absoluteMaxEmailAccounts,
  planIncludedEmailAccounts,
  pricePerSlot,
  currencySymbol,
  currencyFormat,
  billingCycle,
  subscriptionLevel,
  creditCardInfo,
  nextBillingDate,
  billingPeriodStart,
  stripePublicKey,
  billingInfo,
  userEmail,
  cardholderName,
  hasCreditCard,
  filter,
}: ConnectEmailAccountsSheetProps) {
  const [addSlotsModalOpen, setAddSlotsModalOpen] = useState(false)

  const managedAdvantages = [
    i18n.t("js.email_accounts_center.connect.managed.advantages.one"),
    i18n.t("js.email_accounts_center.connect.managed.advantages.two"),
    i18n.t("js.email_accounts_center.connect.managed.advantages.three"),
    i18n.t("js.email_accounts_center.connect.managed.advantages.four"),
    i18n.t("js.email_accounts_center.connect.managed.advantages.five"),
  ]

  // Bring OAuth callbacks (Gmail/Outlook) back to Email Accounts Center and request opening
  // the newly connected account sidepanel. We do this by using a special sentinel value
  // (`email_account_id=latest`) that the center resolves to the newest account on load.
  const redirectUrl = encodeURIComponent(
    `${campaigns_email_accounts_center_path()}?email_account_id=latest`,
  )

  const getTitle = () => {
    if (filter === "managed") {
      return i18n.t("js.email_accounts_center.connect.managed_title")
    }
    if (filter === "connected") {
      return i18n.t("js.email_accounts_center.connect.connected_title")
    }
    return i18n.t("js.email_accounts_center.connect.title")
  }

  const getDescription = () => {
    if (filter === "managed") {
      return i18n.t("js.email_accounts_center.connect.managed_description")
    }
    if (filter === "connected") {
      return i18n.t("js.email_accounts_center.connect.connected_description")
    }
    return i18n.t("js.email_accounts_center.connect.description")
  }

  const handleSmtpImapClick = () => {
    // Open the modal for SMTP/IMAP connection
    const modal = document.getElementById("smtp-imap-modal")
    if (modal) {
      const turboFrame = modal.querySelector("turbo-frame")
      if (turboFrame) {
        turboFrame.setAttribute("src", new_smtp_imap_path())
      }
      // @ts-ignore - Bootstrap modal
      const bsModal = new window.bootstrap.Modal(modal)
      bsModal.show()
    }
  }

  const handleAddSlotsClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setAddSlotsModalOpen(true)
  }

  const renderQuotaAlert = () => {
    if (!isQuotaReached) return null

    const alertTitle = i18n.t(
      "js.email_accounts_center.connect.quota_alert.title",
    )

    if (isPremium) {
      return (
        <Alert variant="info" className="mb-4">
          <AlertIcon>
            <Icon icon="info-circle" />
          </AlertIcon>
          <AlertContent>
            <AlertTitle>{alertTitle}</AlertTitle>
            <p className="text-sm">
              {i18n.t(
                "js.email_accounts_center.connect.quota_alert.description_prefix",
              )}{" "}
              <a href="#" onClick={handleAddSlotsClick}>
                {i18n.t(
                  "js.email_accounts_center.connect.quota_alert.add_email_accounts",
                )}
              </a>{" "}
              {i18n.t("js.or")}{" "}
              <a
                href={upgrade_welcome_index_path({
                  from: "email_accounts_settings",
                })}
              >
                {i18n.t(
                  "js.email_accounts_center.connect.quota_alert.upgrade_plan",
                )}
              </a>
              .
            </p>
          </AlertContent>
        </Alert>
      )
    }

    return (
      <Alert variant="info" className="mb-4">
        <AlertIcon>
          <Icon icon="info-circle" />
        </AlertIcon>
        <AlertContent>
          <AlertTitle>{alertTitle}</AlertTitle>
          <p
            className="text-sm"
            dangerouslySetInnerHTML={{
              __html: i18n.t(
                "js.email_accounts_center.connect.quota_alert.free_description_html",
                {
                  upgrade_path: upgrade_welcome_index_path({
                    from: "email_accounts_settings",
                  }),
                },
              ),
            }}
          />
        </AlertContent>
      </Alert>
    )
  }

  const renderConnectionButtons = () => {
    if (isQuotaReached) {
      return (
        <>
          <Button fullWidth className="mb-2" disabled>
            <img
              src={googleIcon}
              alt=""
              className="mr-2"
              style={{ width: "1.6rem", height: "1.6rem" }}
            />
            {i18n.t("js.sign_in_google")}
          </Button>
          <Button fullWidth className="mb-2" disabled>
            <img
              src={microsoftIcon}
              alt=""
              className="mr-2"
              style={{ width: "1.6rem", height: "1.6rem" }}
            />
            {i18n.t("js.sign_in_microsoft")}
          </Button>
          <Button fullWidth className="mb-2" disabled>
            <Icon icon="plus" className="mr-2" />
            {i18n.t(
              "js.email_accounts_center.connect.existing.connect_smtp_imap",
            )}
          </Button>
        </>
      )
    }

    return (
      <>
        <Button fullWidth className="mb-2" asChild>
          <a
            href={`/auth/gmail?redirect_url=${redirectUrl}`}
            data-turbo-frame="_top"
          >
            <img
              src={googleIcon}
              alt=""
              className="mr-2"
              style={{ width: "1.6rem", height: "1.6rem" }}
            />
            {i18n.t("js.sign_in_google")}
          </a>
        </Button>
        <Button fullWidth className="mb-2" asChild>
          <a
            href={`/auth/outlook?redirect_url=${redirectUrl}`}
            data-turbo-frame="_top"
          >
            <img
              src={microsoftIcon}
              alt=""
              className="mr-2"
              style={{ width: "1.6rem", height: "1.6rem" }}
            />
            {i18n.t("js.sign_in_microsoft")}
          </a>
        </Button>
        <Button fullWidth className="mb-2" onClick={handleSmtpImapClick}>
          <Icon icon="plus" className="mr-2" />
          {i18n.t(
            "js.email_accounts_center.connect.existing.connect_smtp_imap",
          )}
        </Button>
      </>
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" overlay={false} className="p-0">
        <SheetHeader className="mb-0">
          <SheetHeading>
            <SheetTitle className="mb-1">{getTitle()}</SheetTitle>
            <SheetDescription>{getDescription()}</SheetDescription>
          </SheetHeading>
        </SheetHeader>

        <SheetBody>
          {/* Managed email accounts promotion */}
          {(filter === undefined || filter === "managed") && (
            <div className="connect-managed-promotion">
              <div className="d-flex align-items-center mb-2">
                <h3 className="text-md-medium">
                  {i18n.t("js.email_accounts_center.connect.managed.title")}
                </h3>
                {filter !== "managed" && (
                  <Tag variant="info" className="ml-auto">
                    <TagLabel>
                      {i18n.t(
                        "js.email_accounts_center.connect.managed.recommended",
                      )}
                    </TagLabel>
                  </Tag>
                )}
              </div>
              <ul className="connect-managed-promotion__advantages text-sm">
                {managedAdvantages.map((advantage, index) => (
                  <li
                    key={index}
                    className="connect-managed-promotion__advantage"
                  >
                    <Icon icon="check" size="sm" className="text-success" />
                    {advantage}
                  </li>
                ))}
              </ul>
              <Button variant="primary" fullWidth className="mt-4" asChild>
                <a href={email_accounts_mailboxes_path()} data-turbo="false">
                  {i18n.t("js.email_accounts_center.connect.managed.cta")}
                  <Icon icon="arrow-right" size="sm" className="ml-2" />
                </a>
              </Button>
            </div>
          )}

          {filter === undefined && <SeparatorWithWord className="my-6" />}

          {/* Existing email accounts connection */}
          {(filter === undefined || filter === "connected") && (
            <>
              {filter === undefined && (
                <>
                  <h3 className="text-md-medium mb-1">
                    {i18n.t("js.email_accounts_center.connect.existing.title")}
                  </h3>
                  <p className="text-xs text-body-secondary mb-4">
                    {i18n.t(
                      "js.email_accounts_center.connect.existing.description",
                    )}
                  </p>
                </>
              )}

              {renderQuotaAlert()}
              {renderConnectionButtons()}
            </>
          )}
        </SheetBody>
      </SheetContent>

      <AddEmailAccountSlotsModal
        isOpen={addSlotsModalOpen}
        onClose={() => setAddSlotsModalOpen(false)}
        currentEmailAccountsCount={currentEmailAccountsCount}
        currentMaxEmailAccounts={currentMaxEmailAccounts}
        absoluteMaxEmailAccounts={absoluteMaxEmailAccounts}
        planIncludedEmailAccounts={planIncludedEmailAccounts}
        pricePerSlot={pricePerSlot}
        currencySymbol={currencySymbol}
        currencyFormat={currencyFormat}
        billingCycle={billingCycle}
        subscriptionLevel={subscriptionLevel}
        creditCardInfo={creditCardInfo}
        nextBillingDate={nextBillingDate}
        billingPeriodStart={billingPeriodStart}
        stripePublicKey={stripePublicKey}
        billingInfo={billingInfo}
        userEmail={userEmail}
        cardholderName={cardholderName}
        hasCreditCard={hasCreditCard}
      />
    </Sheet>
  )
}

export default ConnectEmailAccountsSheet
